import { useUser } from '@trustpad/launchpad';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { fromWei } from 'web3-utils';
import { useDHControllerContract } from '~features/diamondHands/useDHControllerContract';
import { call, debug } from '~utils';
import { errorTx, successTx } from '~utils/toast';

export enum DHDistributionStatus {
  Ok,
  NoDate,
  PreRegistration,
  Registration,
  Claiming,
  Finished,
}

export enum DHUserStatus {
  Ok,
  NoTier,
  NotEligiblePool,
}

export type DHUserState = {
  status: DHUserStatus;
  isRegistered: boolean;
  isRetroEligible: boolean;
  hasReward: boolean;
  userDH25Share: number;
  sales: string[];
  userDH75Shares: number[];
};

export type DHDistribution = {
  status: DHDistributionStatus;
  currentDistributionIdx: number;
  // Unix timestamp
  start: number;
  // Unix timestamp
  registrationStart: number;
  // Unix timestamp
  end: number;
  registrantsCount: number;
  // Sum of registered AAG participants level multipliers
  totalDH25Share: number;
  // Sum of retrospective eligible AAG participants level multipliers
  totalDH25ShareRetro: number;
  sales: string[];
  totalDH75Shares: number[];
  isRetroSale: boolean[];
} & {
  nextRegistrationStart: number;
};

const toN = (v: any) => parseInt(v as unknown as string, 10);

const filterKeys = <T>(data: Record<any, any>) =>
  Object.keys(data).reduce((acc, key) => {
    if (!key.match(/^[0-9]+$/)) {
      acc[key] = data[key];
    }
    return acc;
  }, {} as T);

export function fromRawUser(raw: DHUserState): DHUserState {
  return {
    ...filterKeys<DHUserState>(raw),
    status: toN(raw.status),
    userDH25Share: toN(fromWei(raw.userDH25Share as unknown as string, 'gwei')),
    userDH75Shares: raw.userDH75Shares.map((v) =>
      toN(fromWei(v as unknown as string)),
    ),
  };
}

export function fromRawDistribution(raw: DHDistribution): DHDistribution {
  return {
    ...filterKeys<DHDistribution>(raw),
    status: toN(raw.status),
    currentDistributionIdx: toN(raw.currentDistributionIdx),
    start: toN(raw.start),
    registrationStart: toN(raw.registrationStart),
    end: toN(raw.end),
    registrantsCount: toN(raw.registrantsCount),
    totalDH25Share: toN(
      fromWei(raw.totalDH25Share as unknown as string, 'gwei'),
    ),
    totalDH25ShareRetro: toN(
      fromWei(raw.totalDH25ShareRetro as unknown as string, 'gwei'),
    ),
    totalDH75Shares: raw.totalDH75Shares.map((v) =>
      toN(fromWei(v as unknown as string)),
    ),
    nextRegistrationStart: toN(raw.nextRegistrationStart),
  };
}

export function useDHDistribution() {
  const { account } = useUser();
  const [dhUser, setDhUser] = useState<DHUserState>({
    status: DHUserStatus.Ok,
    isRegistered: false,
    isRetroEligible: false,
    hasReward: false,
    userDH25Share: 0,
    sales: [],
    userDH75Shares: [],
  });
  const [distribution, setDistribution] = useState<DHDistribution>({
    status: DHDistributionStatus.NoDate,
    currentDistributionIdx: 0,
    start: 0,
    registrationStart: 0,
    end: 0,
    registrantsCount: 0,
    totalDH25Share: 0,
    totalDH25ShareRetro: 0,
    sales: [],
    totalDH75Shares: [],
    nextRegistrationStart: 0,
    isRetroSale: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const dhController = useDHControllerContract();

  useEffect(() => {
    if (!dhController) return;
    setIsLoading(true);
    refresh().finally(() => setIsLoading(false));
  }, [dhController]);

  useEffect(() => {
    if (!account || !dhController) return;
    refreshUser();
  }, [account, dhController]);

  function refresh() {
    const nextDistrDate = dayjs().add(1, 'month');
    // TODO: ensure we always genewrate an ID in one way
    const nextDistributionIdx = parseInt(
      `${nextDistrDate.year()}${nextDistrDate.month()}01`,
      10,
    );

    return Promise.all([
      call(dhController, 'getDistribution', [0]),
      call(dhController, 'getDistribution', [nextDistributionIdx]),
    ]).then(([rawDistr, nextRawDistr]) => {
      debug('rawDistribution', filterKeys(rawDistr));
      setDistribution({
        ...fromRawDistribution(rawDistr),
        nextRegistrationStart: nextRawDistr.registrationStart,
      });
    });
  }

  function refreshUser() {
    return call(dhController, 'getUserDHState', [account]).then((raw) => {
      debug('rawUserDHStatus', filterKeys(raw));
      setDhUser(fromRawUser(raw));
    });
  }

  function register() {
    setIsLoading(true);
    dhController.methods
      .register()
      .send({ from: account })
      .then((tx) => {
        successTx({
          content: 'You registered for the Diamond Hands distribution.',
          tx: tx,
        });
      })
      .catch((e) => {
        errorTx({
          content: `Failed to register for the Diamond Hands distribution.`,
          error: e,
        });
      })
      .finally(() => setIsLoading(false));
  }

  return {
    distribution,
    dhUser,
    isLoading,
    refresh: () => {
      refresh();
      refreshUser();
    },
    register,
  };
}
